import type * as React from 'react';
import { AlertTriangle, Construction, Inbox, Lock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Mono } from '@/components/ui/primitives';
import { isApiError, isUnbuiltEndpoint, messageFor } from '@/api/errors';
import { cn } from '@/lib/utils';

/**
 * Loading, empty and error, written once.
 *
 * A skeleton rather than a centred spinner, because the shape of the page is
 * known before the data is and drawing it early is what stops the layout
 * jumping when the response lands.
 */

export function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return (
    <div
      className={cn('animate-pulse rounded bg-surface-sunken', className)}
      aria-hidden="true"
      {...props}
    />
  );
}

export function TableSkeleton({
  rows = 6,
  columns = 4,
}: {
  rows?: number;
  columns?: number;
}): React.JSX.Element {
  return (
    <div role="status" aria-label="Loading" className="divide-y divide-line">
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="flex items-center gap-6 px-4 py-3.5">
          {Array.from({ length: columns }, (_, column) => (
            <Skeleton
              key={column}
              className="h-3.5"
              style={{ width: `${[28, 18, 14, 10, 12, 16][column % 6] ?? 12}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function CardSkeleton({ className }: { className?: string }): React.JSX.Element {
  return (
    <div
      role="status"
      aria-label="Loading"
      className={cn('space-y-3 rounded-card border border-line bg-surface p-5', className)}
    >
      <Skeleton className="h-3 w-24" />
      <Skeleton className="h-7 w-32" />
      <Skeleton className="h-3 w-40" />
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  /** Say what to do next. "No data" is a dead end. */
  description: string;
  action?: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
}

export function EmptyState({
  title,
  description,
  action,
  icon: Icon = Inbox,
}: EmptyStateProps): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-4 rounded-full border border-line bg-surface-sunken p-3">
        <Icon className="size-5 text-ink-faint" />
      </div>
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      <p className="mt-1 max-w-sm text-sm text-ink-soft">{description}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  /** What the admin was trying to reach, for the "not built yet" wording. */
  endpoint?: string;
}

/**
 * One error surface for every screen.
 *
 * Three cases are told apart, because they need three different actions:
 * a role refusal (nothing to retry), an endpoint the API has not built yet
 * (nothing to retry either, and not a fault), and a genuine failure (retry).
 */
export function ErrorState({ error, onRetry, endpoint }: ErrorStateProps): React.JSX.Element {
  if (isUnbuiltEndpoint(error)) {
    return (
      <EmptyState
        icon={Construction}
        title="This screen is waiting on the API"
        description={
          endpoint
            ? `The panel is built and ready. ${endpoint} is not implemented yet, so there is nothing to show.`
            : 'The panel is built and ready. The endpoint behind it is not implemented yet.'
        }
      />
    );
  }

  if (isApiError(error) && (error.status === 403 || error.code === 'INSUFFICIENT_ROLE')) {
    return (
      <EmptyState
        icon={Lock}
        title="Your role does not allow this"
        description="Ask a super admin if you need access to this section."
      />
    );
  }

  return (
    <div role="alert" className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-4 rounded-full border border-danger/30 bg-danger-wash p-3">
        <AlertTriangle className="size-5 text-danger" />
      </div>
      <h3 className="text-sm font-semibold text-ink">That did not load</h3>
      <p className="mt-1 max-w-md text-sm text-ink-soft">{messageFor(error)}</p>
      {isApiError(error) ? <Mono className="mt-3">{error.code}</Mono> : null}
      {onRetry ? (
        <Button className="mt-5" variant="secondary" onClick={onRetry}>
          <RefreshCw aria-hidden="true" />
          Try again
        </Button>
      ) : null}
    </div>
  );
}
